import type { ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@/components/ui/table'
import type { EngagementEvent } from '@/lib/engagement-event-registrations'
import { RegistrationChange } from './registration-comparison-figures'

const columns = [
  ['event', 'Event'],
  ['date', 'Event date'],
  ['places', 'Places'],
  ['last_year', 'Last year'],
  ['change', 'Change'],
  ['checked_in', 'Checked in'],
]
export function EngagementEventTable<Row extends EngagementEvent>({
  rows,
  sort,
  onSort,
  onOpen,
  eventHref,
  sortableColumns,
  initialSortDirection = 'asc',
  eventCaption,
}: {
  rows: Row[]
  sort: string
  onSort: (value: string) => void
  onOpen: (row: Row, trigger: HTMLElement) => void
  eventHref?: (row: Row) => string | undefined
  sortableColumns?: readonly string[]
  initialSortDirection?: 'asc' | 'desc'
  // A secondary line under the event name (the portal's invoice link for a Billing contact).
  eventCaption?: (row: Row) => ReactNode
}) {
  const [key, direction] = sort.split(':')
  return (
    <Table aria-label="Event registrations" className="relative">
      <TableHeader>
        <TableRow>
          {columns.map(([id, label], index) =>
            sortableColumns && !sortableColumns.includes(id) ? (
              <TableHead
                key={id}
                className={index > 1 ? 'whitespace-nowrap text-right' : 'whitespace-nowrap'}
              >
                {label}
              </TableHead>
            ) : (
              <SortableTableHead
                key={id}
                active={key === id}
                direction={direction === 'desc' ? 'descending' : 'ascending'}
                initialDirection={initialSortDirection === 'desc' ? 'descending' : 'ascending'}
                onToggle={() =>
                  onSort(
                    `${id}:${key === id ? (direction === 'asc' ? 'desc' : 'asc') : initialSortDirection}`,
                  )
                }
                className={index > 1 ? 'whitespace-nowrap text-right' : 'whitespace-nowrap'}
              >
                {label}
              </SortableTableHead>
            ),
          )}
          <TableHead className="w-10">
            <span className="sr-only">Open</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.event.id}
            className="cursor-pointer hover:bg-primary/5"
            onClick={(event) => onOpen(row, event.currentTarget.querySelector('button')!)}
          >
            <TableCell className="whitespace-nowrap">
              <EventName row={row} eventHref={eventHref} onOpen={onOpen} />
              {eventCaption?.(row)}
            </TableCell>
            <TableCell className="whitespace-nowrap">{row.event.start_date_label}</TableCell>
            <TableCell className="text-right tabular-nums">{row.places}</TableCell>
            <TableCell className="whitespace-nowrap text-right tabular-nums">
              {row.last_year ?? '—'}
              {row.last_year_caption && (
                <span className="text-caption text-muted-foreground"> {row.last_year_caption}</span>
              )}
            </TableCell>
            <TableCell className="whitespace-nowrap text-right tabular-nums">
              {row.change.badge ? (
                <Badge variant="neutral">{row.change.badge}</Badge>
              ) : (
                <RegistrationChange change={row.change} />
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">{row.checked_in ?? '—'}</TableCell>
            <TableCell>
              {eventHref ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`View people for ${row.event.name}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    onOpen(row, event.currentTarget)
                  }}
                >
                  <ChevronRight aria-hidden="true" className="size-4 text-muted-foreground" />
                </Button>
              ) : (
                <ChevronRight aria-hidden="true" className="ml-auto size-4 text-muted-foreground" />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function EventName<Row extends EngagementEvent>({
  row,
  eventHref,
  onOpen,
}: {
  row: Row
  eventHref?: (row: Row) => string | undefined
  onOpen: (row: Row, trigger: HTMLElement) => void
}) {
  return eventHref ? (
    eventHref(row) ? (
      <a
        className="font-medium text-foreground hover:underline"
        href={eventHref(row)}
        onClick={(event) => event.stopPropagation()}
      >
        {row.event.name}
      </a>
    ) : (
      <span className="font-medium">{row.event.name}</span>
    )
  ) : (
    <Button
      variant="link"
      size="inline"
      className="font-medium text-foreground"
      onClick={(event) => {
        event.stopPropagation()
        onOpen(row, event.currentTarget)
      }}
    >
      {row.event.name}
    </Button>
  )
}
